<template>
  <div class="pa-google">
    <h2 class="pa-google__title">Connect Google Search Console</h2>
    <p class="pa-google__sub">
      This lets us see which keywords your site already ranks for, so we can build content plans.
    </p>

    <div class="pa-formcard">
      <!-- Google account row -->
      <div class="pa-gscrow">
        <div class="pa-gicon" aria-hidden="true">G</div>
        <div class="pa-gscrow__text">
          <b>Google Search Console</b>
          <span v-if="googleAccount">
            Connected as {{ googleAccount.googleName || googleAccount.googleEmail }}
            ({{ googleAccount.googleEmail }})
          </span>
          <span v-else>Not connected. Connect your Google account to continue</span>
        </div>
        <template v-if="googleAccount">
          <span class="pa-status pa-status--green pa-status--pill">Connected</span>
          <button type="button" class="pa-btn pa-btn--out pa-btn--pill" :disabled="busy" @click="changeAccount">
            Change account
          </button>
        </template>
        <google-auth-code-button
          v-else
          label="Connect Google account"
          @code="onAuthCode"
          @error="onAuthError"
        />
      </div>

      <a
        class="pa-google__help"
        href="https://help.pageoptimizer.pro/article/connect-google-search-console-to-pop"
        target="_blank"
        rel="noopener noreferrer"
      >
        <i class="material-icons-outlined" aria-hidden="true">help_outline</i> How to connect GSC
      </a>

      <div v-if="googleAccount && !loadingProperties && properties.length === 0" class="pa-warn">
        <i class="material-icons" aria-hidden="true">warning</i>
        <div class="pa-warn__text">
          <b>No verified properties found for this Google account.</b>
          <div>Make sure your site is verified in Search Console, or connect a different Google account.</div>
        </div>
        <button type="button" class="pa-btn pa-btn--out pa-btn--pill" @click="changeAccount">Change Google account</button>
      </div>

      <div class="pa-kvgrid">
        <div class="pa-kv">
          <span class="pa-kv__label">Domain</span>
          <span class="pa-kv__value">
            <select
              class="pa-sel pa-sel--inline"
              v-model="propertyUrl"
              :disabled="!googleAccount || loadingProperties || busy"
            >
              <option value="">{{ domainPlaceholder }}</option>
              <option
                v-for="p in properties"
                :key="p.url"
                :value="p.url"
                :disabled="!p.matchesProject"
              >{{ p.url }}{{ p.matchesProject ? '' : ' (does not match this project)' }}</option>
            </select>
          </span>
        </div>
        <div class="pa-kv">
          <span class="pa-kv__label">Location</span>
          <span class="pa-kv__value">{{ locationName }} <small>from your site info</small></span>
        </div>
        <div class="pa-kv">
          <span class="pa-kv__label">Language</span>
          <span class="pa-kv__value">{{ languageName }} <small>from your site info</small></span>
        </div>
        <div class="pa-kv">
          <span class="pa-kv__label">Data period</span>
          <span class="pa-kv__value">
            <select class="pa-sel pa-sel--inline" v-model="datePeriod" :disabled="busy">
              <option v-for="p in periodOptions" :key="p.value" :value="p.value">{{ p.label }}</option>
            </select>
          </span>
        </div>
        <div v-if="googleAccount" class="pa-kv">
          <span class="pa-kv__label">First data pull</span>
          <span class="pa-kv__value">After setup completes</span>
        </div>
        <div v-if="googleAccount" class="pa-kv">
          <span class="pa-kv__label">Pull schedule</span>
          <span class="pa-kv__value">Monthly</span>
        </div>
      </div>

      <div class="pa-consent">
        <label class="pa-check" :class="{ 'pa-check--sel': monitoringEnabled }" @click.prevent="toggleMonitoring">
          <span class="pa-check__box"><i v-if="monitoringEnabled" class="material-icons">check</i></span>
          <span>
            <b>Enable ongoing performance monitoring</b>
            <div class="pa-consent__text">
              I authorize POP to access my Google Search Console data on an ongoing basis to monitor keyword
              rankings, impressions, clicks, and CTR. POP will check performance monthly and generate Wins
              reports.<br />
              We only access Search Console data, nothing else from your Google account. You can revoke
              access at any time. You can also turn this on later in Settings, and we will remind you.
            </div>
          </span>
        </label>
      </div>

      <div class="pa-terms">
        <label class="pa-check" :class="{ 'pa-check--sel': termsAccepted }" @click.prevent="toggleTerms">
          <span class="pa-check__box"><i v-if="termsAccepted" class="material-icons">check</i></span>
          <span>
            I agree to the
            <a href="#" @click.prevent.stop="termsDoc = 'terms'">POP Authority Terms &amp; Conditions</a>
            and
            <a href="#" @click.prevent.stop="termsDoc = 'policy'">Data Access Policy</a>.
            <span class="pa-req">*</span>
          </span>
        </label>
      </div>
      <wg-terms-modal v-if="termsDoc" :doc="termsDoc" @close="termsDoc = null" />

      <div class="pa-gsclock">
        <i class="material-icons" aria-hidden="true">lock</i>
        We only access Google Search Console data. Nothing else.
      </div>
    </div>

    <p v-if="error" class="pa-err">{{ error }}</p>
    <p v-if="busy" class="pa-hint"><span class="pa-spin"></span> {{ busyLabel }}</p>

    <div class="pa-formnav">
      <button type="button" class="pa-btn pa-btn--orange-out pa-btn--pill" :disabled="busy" @click="$emit('back')">
        <i class="material-icons" aria-hidden="true">arrow_back</i>Back
      </button>
      <div class="pa-formnav__right">
        <button
          v-if="isAdmin"
          type="button"
          class="pa-btn pa-btn--out pa-btn--pill pa-btn--admin"
          :disabled="busy"
          @click="showForceConfirm = true"
        >
          <i class="material-icons" aria-hidden="true">task_alt</i>
          Mark step complete (admin)
        </button>
        <button type="button" class="pa-btn pa-btn--out pa-btn--pill" :disabled="busy" @click="showSkipConfirm = true">
          Skip for now
        </button>
        <button
          type="button"
          class="pa-btn pa-btn--orange pa-btn--pill"
          :disabled="!ready || busy"
          @click="finish"
        >Finish setup &amp; start my audit</button>
      </div>
    </div>

    <!-- Skip confirm (design pa-app.jsx Modal: icon, muted body, Cancel + orange confirm) -->
    <div v-if="showSkipConfirm" class="pa-ovl" role="dialog" aria-modal="true" @click.self="showSkipConfirm = false">
      <div class="pa-cmodal">
        <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">link_off</i></div>
        <h3 class="pa-cmodal__title">Skip connecting Google for now?</h3>
        <p class="pa-cmodal__body">
          Your audit still starts, but without your Search Console data it will be less precise. You can connect
          Google later from Settings, or email devs@pageoptimizer.pro to arrange access another way.
        </p>
        <div class="pa-cmodal__acts">
          <button type="button" class="pa-btn pa-btn--out" :disabled="busy" @click="showSkipConfirm = false">Back</button>
          <button type="button" class="pa-btn pa-btn--orange" :disabled="busy" @click="skip">Skip and start my audit</button>
        </div>
      </div>
    </div>

    <!-- Admin force-complete confirm (the button's task_alt icon) -->
    <div v-if="showForceConfirm && isAdmin" class="pa-ovl" role="dialog" aria-modal="true" @click.self="showForceConfirm = false">
      <div class="pa-cmodal">
        <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">task_alt</i></div>
        <h3 class="pa-cmodal__title">Mark the Google step as complete?</h3>
        <p class="pa-cmodal__body">
          This sets all onboarding steps as completed and updates the Asana onboarding tasks. No Search Console
          data is imported.
        </p>
        <div class="pa-cmodal__acts">
          <button type="button" class="pa-btn pa-btn--out" :disabled="busy" @click="showForceConfirm = false">Cancel</button>
          <button type="button" class="pa-btn pa-btn--orange" :disabled="busy" @click="forceComplete">Mark step complete</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { showPopAuthorityToast } from '@/utils/popAuthority'
import GoogleAuthCodeButton from '@/components/SitesSingleView/WhiteGlove/Settings/GoogleAuthCodeButton.vue'
import WgTermsModal from '@/components/SitesSingleView/WhiteGlove/Settings/WgTermsModal.vue'
import gscAuditImportMixin from '@/components/SitesSingleView/WhiteGlove/SiteContentAuditWidget/gscAuditImportMixin.js'
import { restoreBackendGoogleSession } from '@/utils/search-console-helper.js'
import { DATE_RANGE_OPTIONS } from '@/components/Campaigns/keyword-wizard/DateRangeSelect.vue'
import { locationNameForCode, languageNameForCode } from './popAuthorityFormSchema.js'

const PERIOD_VALUES = ['last_28_days', 'last_3_months', 'last_6_months']

/**
 * Step 4 "Connect Google" (design GscStep): one Google popup gives both the server-side Search
 * Console connection (tracking / Wins, with the monitoring + terms consents) and the audit keyword
 * import (gscAuditImportMixin, same data the White Glove audit step produces). The step can be
 * skipped by the user (`gscSkipped`) or force-completed by mission-control admins.
 */
export default {
  name: 'PopAuthorityGoogleStep',
  components: { GoogleAuthCodeButton, WgTermsModal },
  mixins: [gscAuditImportMixin],
  props: {
    site: { type: Object, default: null },
    activeAudit: { type: Object, required: true },
    isAdmin: { type: Boolean, default: false },
  },
  data () {
    return {
      googleAccount: null,
      properties: [],
      loadingProperties: false,
      propertyUrl: '',
      datePeriod: 'last_28_days',
      monitoringEnabled: true,
      termsAccepted: false,
      termsDoc: null, // 'terms' | 'policy' | null
      finishing: false,
      importSucceeded: false,
      skipping: false,
      error: '',
      showSkipConfirm: false,
      showForceConfirm: false,
    }
  },
  computed: {
    step0 () {
      return (this.activeAudit && this.activeAudit.formData && this.activeAudit.formData.step0) || {}
    },
    locationCode () {
      const raw = this.step0.location_code
      if (raw === undefined || raw === null || raw === '') return 2840
      const code = parseInt(raw, 10)
      return Number.isFinite(code) ? code : raw
    },
    languageCode () {
      return this.step0.language_code || 'en'
    },
    locationName () {
      return locationNameForCode(this.locationCode) || String(this.locationCode)
    },
    languageName () {
      return languageNameForCode(this.languageCode) || this.languageCode
    },
    periodOptions () {
      return DATE_RANGE_OPTIONS.filter((o) => PERIOD_VALUES.includes(o.value))
    },
    domainPlaceholder () {
      if (!this.googleAccount) return 'Connect first'
      if (this.loadingProperties) return 'Loading properties…'
      return this.properties.length ? 'Select property' : 'No properties found'
    },
    ready () {
      return Boolean(this.googleAccount && this.propertyUrl && this.termsAccepted)
    },
    busy () {
      return this.finishing || this.skipping || this.submitting
    },
    busyLabel () {
      if (this.skipping) return 'Saving…'
      if (this.submitting) return 'Importing your Search Console data. This can take a minute…'
      return 'Connecting Google Search Console…'
    },
  },
  watch: {
    submitting (val, oldVal) {
      // the mixin finished (success emits before this flips); anything else is a failed import
      if (oldVal && !val && this.finishing && !this.importSucceeded) {
        this.finishing = false
        this.error = 'The Google data import did not complete. Try again, or skip this step for now.'
      }
    },
    'site.id' (id) {
      if (id && this.googleAccount && !this.properties.length && !this.loadingProperties) {
        this.loadProperties()
      }
    },
  },
  created () {
    // gscAuditImportMixin emits `success` on this component once the audit import is stored
    this.$on('success', this.onImportSuccess)
  },
  mounted () {
    // Reuse a Google account connected elsewhere (Settings / another site): no second popup
    this.$store.dispatch('getGoogleAccessToken')
      .then((response) => {
        const data = response && response.data
        if (this.googleAccount || !data || data.status !== 'SUCCESS' || !data.googleEmail) return
        this.googleAccount = {
          id: null,
          googleEmail: data.googleEmail,
          googleName: data.googleName || data.googleEmail,
        }
        this.loadProperties()
      })
      .catch(() => {})
  },
  methods: {
    toggleMonitoring () {
      if (this.busy) return
      this.monitoringEnabled = !this.monitoringEnabled
    },
    toggleTerms () {
      if (this.busy) return
      this.termsAccepted = !this.termsAccepted
    },
    changeAccount () {
      // Only clears the picked account locally; the popup opens when the user clicks Connect again.
      this.googleAccount = null
      this.properties = []
      this.propertyUrl = ''
      this.error = ''
    },
    onAuthCode (code) {
      this.error = ''
      this.$store.dispatch('connectGoogleAccount', { authCode: code })
        .then((response) => {
          const data = response && response.data
          if (data && data.status === 'SUCCESS') {
            this.googleAccount = data.googleAccount
            this.propertyUrl = ''
            showPopAuthorityToast(this, 'Google account connected')
            this.loadProperties()
          } else {
            this.error = (data && data.msg) || 'Could not connect the Google account'
          }
        })
        .catch((e) => {
          this.error = (e.response && e.response.data && e.response.data.msg) || 'Could not connect the Google account'
        })
    },
    onAuthError (message) {
      this.error = message || 'Google sign-in failed'
    },
    loadProperties () {
      if (!this.site || !this.site.id) return
      this.loadingProperties = true
      this.$store.dispatch('listGscProperties', this.site.id)
        .then((response) => {
          const data = response && response.data
          if (data && data.status === 'SUCCESS') {
            const properties = data.properties || []
            // matching properties first; pre-select the sc-domain one like the Settings wizard
            this.properties = [
              ...properties.filter((p) => p.matchesProject),
              ...properties.filter((p) => !p.matchesProject),
            ]
            if (!this.propertyUrl) {
              const matching = this.properties.filter((p) => p.matchesProject)
              const preferred = matching.find((p) => p.url.startsWith('sc-domain:')) || matching[0]
              if (preferred) this.propertyUrl = preferred.url
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loadingProperties = false
        })
    },
    async finish () {
      if (!this.ready || this.busy) return
      this.error = ''
      this.importSucceeded = false
      this.finishing = true
      try {
        // 1. server-side connection (tracking / Wins) with the consents given on this step
        const body = {
          propertyUrl: this.propertyUrl,
          locationCode: this.locationCode,
          languageCode: this.languageCode,
          countryEnabled: 1,
          datePeriod: this.datePeriod,
          monitoringEnabled: this.monitoringEnabled ? 1 : 0,
          termsAccepted: 1,
        }
        if (this.googleAccount && this.googleAccount.id) {
          body.googleAccountId = this.googleAccount.id
        }
        const connection = await this.$store.dispatch('connectGsc', { siteId: this.site.id, body })
        if (!(connection.data && connection.data.status === 'SUCCESS')) {
          throw new Error((connection.data && connection.data.msg) || 'Could not connect Search Console')
        }
        this.hasActiveWinsConnection = true

        // 2. terms acceptance on the audit
        const terms = await this.$store.dispatch('updateSiteContentAudit', {
          auditId: this.activeAudit.id,
          body: { termsAccepted: true },
        })
        if (!(terms.data && terms.data.status === 'SUCCESS')) {
          throw new Error('Could not save your terms acceptance')
        }

        // 3. the audit import runs in the browser with the backend-held Google token
        const googleUser = await restoreBackendGoogleSession(this.$store, { force: true })
        if (!googleUser) {
          throw new Error('Could not access your Google account. Please connect it again.')
        }
        this.setConfig('wizardDomain', this.propertyUrl)
        this.setConfig('wizardLocation', this.locationCode)
        this.setConfig('wizardLanguage', this.languageCode)
        this.setConfig('wizardIsCountryEnabled', true)
        this.setConfig('wizardIsBrandedEnabled', false)
        const period = this.availablePeriods.find((p) => p.value === this.datePeriod)
        if (period) this.setConfig('wizardPeriod', period)

        await this.loadDomainLevelCampaign()
        this.googleSiteUrl = this.propertyUrl
        this.onStart() // completes through onImportSuccess (or the `submitting` watcher on failure)
      } catch (e) {
        this.error = (e && e.message) || 'Something went wrong. Please try again.'
        this.finishing = false
      }
    },
    onImportSuccess () {
      this.importSucceeded = true
      this.finishing = false
      this.$store.commit('triggerRefreshKeywordResearchCard')
      this.$emit('done')
    },
    skip () {
      if (this.busy) return
      this.skipping = true
      this.error = ''
      return this.$store.dispatch('updateSiteContentAudit', {
        auditId: this.activeAudit.id,
        body: { gscSkipped: true },
      })
        .then((response) => {
          const data = response && response.data
          if (data && data.status === 'SUCCESS') {
            this.showSkipConfirm = false
            this.$emit('done')
          } else {
            this.error = (data && data.msg) || 'Could not skip this step'
          }
        })
        .catch(() => {
          this.error = 'Could not skip this step'
        })
        .finally(() => {
          this.skipping = false
        })
    },
    forceComplete () {
      if (this.busy) return
      this.skipping = true
      this.error = ''
      return this.$store.dispatch('updateSiteContentAudit', {
        auditId: this.activeAudit.id,
        body: { force_onboarding_completed: 1 },
      })
        .then((response) => {
          const data = response && response.data
          if (data && data.status === 'SUCCESS') {
            this.showForceConfirm = false
            this.$emit('done')
          } else {
            this.error = (data && data.msg) || 'Could not complete this step'
          }
        })
        .catch(() => {
          this.error = 'Could not complete this step'
        })
        .finally(() => {
          this.skipping = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup.scss';
@import './designModal.scss';

.pa-google {
  max-width: 860px;
  color: $pa-teal;

  &__title {
    font-size: 1.4rem;
    font-weight: 800;
    margin: 0 0 6px;
    color: $pa-teal;
  }

  &__sub {
    color: $pa-muted;
    margin: 0 0 20px;
  }

  &__help {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: $pa-teal;
    font-weight: 600;
    font-size: 0.85rem;
    text-decoration: none;
    margin: 0 0 14px;

    &:hover {
      color: $pa-accent;
    }

    .material-icons-outlined {
      font-size: 18px;
    }
  }
}

.pa-gscrow {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  &__text {
    flex: 1;
    min-width: 200px;

    b {
      display: block;
      font-size: 1rem;
    }

    span {
      color: $pa-muted;
      font-size: 0.88rem;
      word-break: break-word;
    }
  }
}

.pa-gicon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #1a73e8;
  color: #fff;
  font-weight: 800;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.pa-warn {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  border: 1.5px solid #ffdfc2;
  background: #fff7f0;
  border-radius: 10px;
  padding: 12px 16px;
  margin: 0 0 14px;
  flex-wrap: wrap;

  .material-icons {
    color: $pa-accent;
  }

  &__text {
    flex: 1;
    min-width: 200px;
    font-size: 0.88rem;

    div {
      color: $pa-muted;
      margin-top: 2px;
    }
  }
}

/* design .pa-2col: two equal columns; minmax(0, 1fr) so a long property name in the domain select
   cannot widen its column (1fr alone lets the select's intrinsic width win) */
.pa-kvgrid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.pa-kv {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: $pa-box-bg;
  border: 1px solid $pa-border-soft;
  border-radius: 10px;
  padding: 11px 16px;
  min-height: 46px;
  min-width: 0;

  &__label {
    color: $pa-muted;
    flex: none;
  }

  &__value {
    font-weight: 800;
    text-align: right;
    min-width: 0;

    small {
      color: $pa-faint;
      font-size: 0.75rem;
      font-weight: 600;
    }

    .pa-sel--inline {
      max-width: 100%;
      min-width: 0;
    }
  }
}

.pa-consent {
  border: 1.5px solid $pa-info;
  background: $pa-info-bg;
  border-radius: 12px;
  padding: 14px 16px;
  margin-top: 16px;

  &__text {
    font-size: 0.86rem;
    color: $pa-muted;
    margin-top: 4px;
    font-weight: 400;
  }
}

.pa-terms {
  margin-top: 14px;
}

.pa-gsclock {
  display: flex;
  align-items: center;
  gap: 8px;
  background: $pa-box-bg;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 0.85rem;
  color: $pa-muted;
  margin-top: 14px;

  .material-icons {
    font-size: 16px;
  }
}
</style>
